import EventEmitter from 'events'

const CORTEX_URL = 'wss://emotivcortex.com:54321'

const safeParse = (msg) => { try { return JSON.parse(msg) } catch (_) { return null } }
const makeChainablePromise = (parent) => {
  // console.log('Parent: ' + JSON.stringify(parent))
  class ChainablePromise extends Promise {
    static proxy (method) {
      // console.log('this.prototype: ' + this.prototype)
      if (this.prototype[method]) return
      // console.log('method: ' + method + 'this method should be in PARENT instance')
      if (parent[method]) console.log('method [ ' + method + ' ] is in PARENT')
      this.prototype[method] = function (...args) {
        return this.then(() => parent[method](...args))
      }
    }
  }
  const proto = parent.constructor ? parent.constructor.prototype : {}
  // TODO: debug proto maybe problem in this place
  console.log("The PROTO is: " + JSON.stringify(proto));
  
  for (const method of Object.getOwnPropertyNames(proto)) {
    if (typeof parent[method] === 'function' &&
        method[0] !== '_' &&
        method !== 'constructor') {
      // console.log('! Wrapping method [ ' + method + ' ] with ChainablePromise.proxy')
      ChainablePromise.proxy(method)
    }
  }
  return ChainablePromise
}

class JSONRPCError extends Error {
  constructor (err) {
    super(err.message)
    this.name = this.constructor.name
    this.message = err.message
    this.code = err.code
  }
  toString () {
    return super.toString() + ` (${this.code})`
  }
}

export default class Cortex extends EventEmitter {
  constructor (options = {}) {
    super()
    this.options = options
    this.ws = new WebSocket(CORTEX_URL)
    this.msgId = 0
    this.requests = {}
    this.streams = {}
    this.ws.addEventListener('message', this._onmsg.bind(this))
    this.ws.addEventListener('close', () => {
      this._log('ws: Socket closed')
      this.call = () => this.APIResult.reject(new Error('socket closed'))
    })
    this.verbose = options.verbose !== null ? options.verbose : 1
    this.APIResult = makeChainablePromise(this)
    this.ready =
      new this.APIResult(resolve => this.ws.addEventListener('open', resolve))
        .then(() => this._log('ws: Socket opened'))
        .then(() => this.call('inspectApi')) // workaround
        // .call('inspectApi') FIXME: this method should work
        .then(methods => {
          for (const m of methods) this.defineMethod(m.methodName, m.params)
          this._log(`rpc: Added ${methods.length} methods from inspectApi`)
        })
        .catch((error) => console.log('ERROR: ' + error))
  }
  _onmsg (msg) {
    const data = safeParse(msg.data)
    if (!data) return this._warn('unparseable message', msg)

    this._debug('ws: <-', msg.data)

    if ('id' in data) {
      const id = data.id
      this._log(`[${id}] <-`, data.result ? 'success' : `error (${data.error.message})`)
      if (this.requests[id]) {
        this.requests[id](data.error, data.result)
      } else {
        this._warn('rpc: Got response for unknown id', id)
      }
    } else if ('sid' in data) {
      const dataKeys = Object.keys(data).filter(k =>
        k !== 'sid' && k !== 'time' && Array.isArray(data[k])
      )
      for (const k of dataKeys) {
        this.emit(k, data) || this._warn('no listeners for stream event', k)
      }
    } else {
      this._log('rpc: Unrecognised data', data)
    }
  }
  _warn (...msg) {
    if (this.verbose > 0) console.warn('[Cortex WARN]', ...msg)
  }
  _log (...msg) {
    if (this.verbose > 1) console.warn('[Cortex LOG]', ...msg)
  }
  _debug (...msg) {
    if (this.verbose > 2) console.warn('[Cortex DEBUG]', ...msg)
  }
  init ({username, password, client_id, client_secret, debit} = {}) {
    const result = this.getUserLogin()
      .then(users => {
        if (users[0] && users[0] !== username) {
          this._log('init: Logging out other user', users[0])
          return this.logout({username: users[0]}).then(() => [])
        }
        if (username) {
          this._log('init: Reusing existing login')
        } else {
          this._log('init: Logging in anonymously')
        }
        return users
      })
      .then(users => {
        if (!users[0] && username && password) {
          this._log('init: Logging in as', username)
          return this.login({username, password, client_id, client_secret})
        }
      })
      .then(this.authorize({client_id, client_secret, debit}).then(({_auth}) => {
        this._log('init: Got auth token')
        this._debug('init: Auth token', _auth)
        this._auth = _auth
      }))

    return result
  }
  close () {
    return new this.APIResult((resolve) => {
      this.ws.close()
      this.ws.once('close', resolve)
    })
  }
  call (method, params = {}) {
    const id = this.msgId++
    const msg = JSON.stringify({jsonrpc: '2.0', method, params, id})
    this.ws.send(msg)
    this._log(`[${id}] -> ${method}`)

    this._debug('ws: ->', msg)
    return new this.APIResult((resolve, reject) => {
      this.requests[id] = (err, data) => {
        delete this.requests[id]
        this._debug('rpc: err', err, 'data', data)
        if (err) return reject(new JSONRPCError(err))
        if (data) return resolve(data)
        return reject(new Error('Invalid JSON-RPC response'))
      }
    })
  }
  defineMethod (methodName, paramDefs = []) {
    if (this[methodName]) return
    const needsAuth = paramDefs.some(p => p.name === '_auth')
    const requiredParams = paramDefs.filter(p => p.required).map(p => p.name)

    this[methodName] = (params = {}) => {
      if (needsAuth && this._auth && !params._auth) {
        params = Object.assign({}, params, {_auth: this._auth})
      }
      const missingParams = requiredParams.filter(p => params[p] == null)
      if (missingParams.length > 0) {
        return this.APIResult.reject(
          new Error(`Missing required params for ${methodName}: ${missingParams.join(', ')}`)
        )
      }
      return this.call(methodName, params)
    }

    this.APIResult.proxy(methodName)
  }
}
Cortex.JSONRPCError = JSONRPCError
Cortex.makeChainablePromise = makeChainablePromise


// WEBPACK FOOTER //
// ./src/lib/cortex.js